import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Alert,
  type AlertColor,
  Box,
  Button,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as tripPackageInventorySlice from '../../../data/tripPackageInventory.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as referenceSlice from '../../../data/reference.slice';
import * as uiSlice from '../../../data/ui.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  DataGridPro,
  type GridCellParams,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import TripPackageRejectDialog from '../notifications/TripPackageRejectDialog';
import dateUtils from '../../../services/DateUtils';
import {
  fetchTimeZonesAsync,
  fetchTripModesAsync,
} from '../../../data/reference.slice';
import userService from '../../../services/userService';
import authService from '../../../services/authService';
import { fetchChargeTypesAsync } from '../../../data/reference.slice';
import { webSocketService } from '../../../services/webSocketService';
import { PackageTripLegItem } from '../../../data/models/trip-leg-package.model';

const PREFIX = 'Tier3TripPackages';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  grid: `${PREFIX}-grid`,
  address: `${PREFIX}-address`,
  line2: `${PREFIX}-line2`,
  actions: `${PREFIX}-actions`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    overflowY: 'auto',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    height: 80,
    backgroundImage: 'url(/bggray.png)',
    backgroundRepeatX: 'repeat',
    backgroundRepeatY: 'no-repeat',
    padding: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(2),
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
  [`& .${classes.address}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.line2}`]: {
    fontWeight: 400,
  },
  [`& .${classes.content}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.actions}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

// test uuid: 2273096b-fc7d-4cfd-bf4b-7a8661654e4b

export default function Tier3TripPackages() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const params = useParams<{ uuid: string }>();

  const rejectReasons = useAppSelector(
    tripPackageInventorySlice.selectTripLegPackageRejectReasons,
  );
  const loadingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectLoadingTripLegPackage,
  );
  const tripPackage = useAppSelector(
    tripPackageInventorySlice.selectTripLegPackage,
  );
  const acceptingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectAcceptingTripPackage,
  );
  const rejectingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectRejectingTripPackage,
  );
  const timezones = useAppSelector(referenceSlice.selectTimeZones);
  const tripModes = useAppSelector(referenceSlice.selectTripModes);
  const transportationAttributes = useAppSelector(
    accountingSlice.selectAllTransportationAttributes,
  );
  const successMsg = useAppSelector(uiSlice.selectSuccessMessage);
  const errorMsg = useAppSelector(uiSlice.selectErrorMessage);
  const offeredTripLegEvent = useAppSelector(
    tripPackageInventorySlice.selectOfferedTripLegEvent,
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>();
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertColor>('success');

  const [packageUUID, setPackageUUID] = useState<string>();
  const [providerID, setProviderID] = useState<number>();
  const [tripPackageRejectOpen, setTripPackageRejectOpen] = useState(false);

  useEffect(() => {
    authService.getTier3Tokens().subscribe((tokens) => {
      dispatch(fetchChargeTypesAsync());
      dispatch(fetchTimeZonesAsync());
      dispatch(fetchTripModesAsync());

      webSocketService.setSessionInfo(
        {
          accessToken: tokens.accessToken,
          accessTokenID: tokens.idToken,
        },
        true,
      );

      userService.getUserInfo().subscribe({
        next: (userInfo) => {
          if (userInfo?.userAccountID) {
            setProviderID(userInfo.transportationProviderID);
            webSocketService.setSessionInfo({
              userAccountID: userInfo.userAccountID,
              loggedInTransportationProviderID:
                userInfo.transportationProviderID,
            });
          }
          webSocketService.connect(dispatch);

          dispatch(
            tripPackageInventorySlice.fetchTripLegPackageRejectReasonsAsync(),
          );
          dispatch(accountingSlice.fetchTransportationAttributesAsync());
          if (params.uuid) {
            setPackageUUID(params.uuid);
            dispatch(
              tripPackageInventorySlice.getTripLegPackageAsync(
                userInfo.transportationProviderID,
                params.uuid,
              ),
            );
          }
        },
        error: (err) => {
          console.warn('error: ', err);
        },
      });
    });
  }, []);

  useEffect(() => {
    if (successMsg) {
      setSnackbarMessage(t(successMsg));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } else if (errorMsg) {
      setSnackbarMessage(t('attestation.expiredToken'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [successMsg, errorMsg]);

  useEffect(() => {
    if (offeredTripLegEvent && providerID && packageUUID) {
      dispatch(
        tripPackageInventorySlice.getTripLegPackageAsync(
          providerID,
          packageUUID,
        ),
      );
      dispatch(tripPackageInventorySlice.setOfferedTripLegEvent(false));
    }
  }, [offeredTripLegEvent, providerID, packageUUID, dispatch]);

  const renderPickupTime = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const isWillCall = params.row.isWillCall;
    const tz = dateUtils.getTimeZoneIana(
      timezones,
      params.row.pickUpAddressTimeZoneID,
    );

    return isWillCall ? (
      <Box>
        <Typography>
          {dateUtils.formatAppointmentDate(params.row.appointmentDate, tz)}
        </Typography>
        <Typography>{t('trips.willCall')}</Typography>
      </Box>
    ) : (
      <Typography>
        {dateUtils.formatPickupDateTime(params.row.pickUpDueTimeUTC, tz)}
      </Typography>
    );
  };

  const renderReservationReferenceID = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    return (
      <Typography>{params.row.reservation.reservationReferenceID}</Typography>
    );
  };

  const renderPickupAddress = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const tripLeg = params.row;
    return (
      <Box className={classes.address}>
        <Typography>{`${tripLeg.pickUpAddressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${tripLeg.pickUpCity}, ${tripLeg.pickUpStateCode} ${tripLeg.pickUpZipCode}`}</Typography>
      </Box>
    );
  };

  const renderDropoffAddress = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const tripLeg = params.row;
    return (
      <Box className={classes.address}>
        <Typography>{`${tripLeg.dropOffAddressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${tripLeg.dropOffCity}, ${tripLeg.dropOffStateCode} ${tripLeg.dropOffZipCode}`}</Typography>
      </Box>
    );
  };

  const renderTransportationAttributes = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const tripMode = tripModes?.find(
      (mode) => mode.tripModeID === params.row.tripModeID,
    );
    const careLevelName = params.row.passengerCareLevelName;

    return (
      <Box>
        <Typography>{tripMode?.tripModeName}</Typography>
        <Typography>{careLevelName}</Typography>
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('notification.pickupTime') || '',
      width: 180,
      sortable: false,
      renderCell: renderPickupTime,
    },
    {
      field: 'reservationID',
      headerName: t('notification.reservationReferenceID') || '',
      width: 180,
      sortable: false,
      renderCell: renderReservationReferenceID,
    },
    {
      field: 'pickupAddress',
      headerName: t('notification.pickupAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('notification.dropoffAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'attributes',
      headerName: t('notification.passengerAttributes') || '',
      flex: 1,
      sortable: false,
      renderCell: renderTransportationAttributes,
    },
  ];

  const acceptTripPackage = () => {
    dispatch(tripPackageInventorySlice.acceptTripPackageAsync(packageUUID!));
  };

  const rejectTripPackage = () => {
    setTripPackageRejectOpen(true);
  };

  const closeTripPackageReject = (reason?: string) => {
    if (reason) {
      dispatch(
        tripPackageInventorySlice.rejectTripPackageAsync(packageUUID!, reason),
      );
    }
    setTripPackageRejectOpen(false);
  };

  const rows = useMemo(() => {
    const tripLegsRaw = tripPackage?.tripLegs;
    const tripLegs = Array.isArray(tripLegsRaw)
      ? tripLegsRaw
      : tripLegsRaw
      ? [tripLegsRaw]
      : [];

    return tripLegs.map((tripLeg) => ({
      ...tripLeg,
      id:
        tripLeg.tripLegReferenceID ??
        tripLeg.tripLegID ??
        `${tripLeg.reservationID}-${tripLeg.tripLegSequence}`,
    }));
  }, [tripPackage?.tripLegs]);

  const canAccept = useMemo(() => {
    return (
      packageUUID &&
      !acceptingTripPackage &&
      !rejectingTripPackage &&
      !successMsg &&
      !errorMsg &&
      rows.length > 0
    );
  }, [
    packageUUID,
    acceptingTripPackage,
    rejectingTripPackage,
    successMsg,
    errorMsg,
    rows.length,
  ]);

  return (
    <StyledBox className={classes.container}>
      <Box component="div" className={classes.header}>
        <h2 className={classes.title}>{t('provider.tripLegPackage')}</h2>
      </Box>
      <Box className={classes.content}>
        <DataGridPro
          loading={loadingTripPackage}
          rows={rows}
          columns={columns}
          autoHeight={true}
          className={classes.grid}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          hideFooter={true}
        />
      </Box>
      <Box className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          onClick={acceptTripPackage}
          disabled={!canAccept}>
          {t('notification.accept')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={rejectTripPackage}
          disabled={!canAccept}>
          {t('notification.decline')}
        </Button>
      </Box>
      <TripPackageRejectDialog
        rejectReasons={rejectReasons}
        open={tripPackageRejectOpen}
        close={closeTripPackageReject}
      />
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}
